import React, { useState } from "react";
import { Typography, Grid, Button, LinearProgress, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import IconButton from "@mui/material/IconButton";
import { useNavigate } from "react-router-dom";
import { useAntropometriaTable } from "../hooks/useAntropometriaTable";

const SORT_OPTIONS = [
  { value: 'createdAt_desc', label: 'Más reciente', model: [{ field: 'createdAt', sort: 'desc' }] },
  { value: 'pacienteId_asc', label: 'ID', model: [{ field: 'pacienteId', sort: 'asc' }] },
  { value: 'paciente_asc', label: 'Nombre', model: [{ field: 'paciente', sort: 'asc' }] },
];

function AntropometriaTable() {
  const { medidas, loading, fetchMedidas } = useAntropometriaTable();
  const navigate = useNavigate();
  const [sortModel, setSortModel] = useState([{ field: 'createdAt', sort: 'desc' }]);

  const currentSort = SORT_OPTIONS.find(
    o => o.model[0]?.field === sortModel[0]?.field && o.model[0]?.sort === sortModel[0]?.sort
  )?.value ?? 'createdAt_desc';

  const columns = [
    { field: 'pacienteId', headerName: 'ID', width: 30 },
    { field: 'paciente', headerName: 'Paciente', width: 180 },
    { field: 'profesional', headerName: 'Profesional', width: 180 },
    {
      field: "createdAt", headerName: "Fecha", width: 160,
      sortComparator: (v1, v2, cp1, cp2) =>
        new Date(cp1.api.getRow(cp1.id).fechaRaw) - new Date(cp2.api.getRow(cp2.id).fechaRaw),
    },
    { field: "talla", headerName: "Talla", width: 100 },
    { field: "peso", headerName: "Peso", width: 100 },
    { field: "perimetroAbdominal1", headerName: "Per Abdo 1", width: 140 },
    { field: "perimetroAbdominal2", headerName: "Per Abdo 2", width: 140 },
    { field: "perimetroAbdominal3", headerName: "Per Abdo 3", width: 140 },
    { field: "mediaPerimetroAbdominal", headerName: "Media Per Abdo", width: 160 },
    { field: "grasa", headerName: "Grasa", width: 100 },
    { field: "nivelGrasa", headerName: "Nivel Grasa", width: 120 },
    { field: "musculo", headerName: "Músculo", width: 100 },
    { field: "imc", headerName: "IMC", width: 100 },
    { field: "bd", headerName: "BD", width: 100 },
    { field: "bi", headerName: "BI", width: 100 },
    { field: "pd", headerName: "PD", width: 100 },
    { field: "pi", headerName: "PI", width: 100 },
    { field: "pesoBrazosPiernas", headerName: "Peso Brazos/Piernas", width: 180 },
    { field: "smi", headerName: "SMI", width: 100 },

    {
      field: "actions",
      headerName: "Acciones",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton color="primary" onClick={() => navigate(`/ver-antropometria/${params.row.codigo}/${params.row.pacienteId}`)}>
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Typography variant="h6" gutterBottom color="#0D6AEE">
        Lista de Mediciones Antropométricas
      </Typography>

      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid item>
          <ToggleButtonGroup
            value={currentSort}
            exclusive
            size="small"
            onChange={(_, val) => {
              const opt = SORT_OPTIONS.find(o => o.value === val);
              if (opt) setSortModel(opt.model);
            }}
          >
            {SORT_OPTIONS.map(o => <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>)}
          </ToggleButtonGroup>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<RestartAltIcon />}
            onClick={fetchMedidas}
          >
            Refrescar
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <LinearProgress />
      ) : (
        <div style={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={medidas}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            slots={{ toolbar: GridToolbar }}
            disableSelectionOnClick
            onRowClick={(params) => navigate(`/ver-antropometria/${params.row.codigo}/${params.row.pacienteId}`)}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
          />
        </div>
      )}
    </>
  );
}

export default AntropometriaTable;
